/* eslint-disable new-cap */
/**
 * REST API を使用したメールテンプレート管理 hooks
 */

import { restClient } from "lib/rest-client";
import { useCallback, useEffect, useRef, useState } from "react";

// =====================================
// Types
// =====================================

export const DeliveryTiming = {
  Now: "now",
  Scheduled: "scheduled",
} as const;

export type DeliveryTiming =
  (typeof DeliveryTiming)[keyof typeof DeliveryTiming];

export type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
  from: string;
  distributionGroupId: string | null;
  deliveryTiming: DeliveryTiming;
  includeSender: boolean;
  createdBy: string;
  createdAt: string;
};

export type EmailTemplateDocuments = {
  items: EmailTemplate[];
  total: number;
};

export type CreateEmailTemplateInput = {
  name: string;
  subject: string;
  body: string;
  from: string;
  distributionGroupId: string | null;
  deliveryTiming: DeliveryTiming;
  includeSender: boolean;
};

export type UpdateEmailTemplateInput = {
  name: string;
  subject: string;
  body: string;
  from: string;
  distributionGroupId: string | null;
  deliveryTiming: DeliveryTiming;
  includeSender: boolean;
};

export type EmailTemplateSearchInput = {
  distributionGroupId?: string;
  deliveryTiming?: DeliveryTiming;
  from?: string;
  name?: string;
};
export type UseEmailTemplatesRestApiOptions = {
  filter?: EmailTemplateSearchInput;
  skip?: boolean;
  onCompleted?: () => void;
};

export type UseEmailTemplatesRestApiResult = {
  data: { emailTemplates: EmailTemplateDocuments } | undefined;
  error: Error | undefined;
  refetch: () => Promise<EmailTemplateDocuments>;
  createEmailTemplate: (
    input: CreateEmailTemplateInput,
  ) => Promise<EmailTemplate>;
  updateEmailTemplate: (
    templateId: string,
    input: UpdateEmailTemplateInput,
  ) => Promise<EmailTemplate>;
  deleteEmailTemplate: (templateId: string) => Promise<void>;
  listLoading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
};

/**
 * メールテンプレート一覧取得/作成/更新/削除 hook
 * @param {UseEmailTemplatesRestApiOptions} options - オプション設定
 * @return {UseEmailTemplatesRestApiResult} 一覧、CRUD関数、ローディング状態、エラー
 */
export function useEmailTemplatesRestApi(
  options: UseEmailTemplatesRestApiOptions = {},
): UseEmailTemplatesRestApiResult {
  const [data, setData] = useState<
    { emailTemplates: EmailTemplateDocuments } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [listLoading, setListLoading] = useState(!options.skip);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const executeApi = useCallback(
    async <T>(
      setLoading: (loading: boolean) => void,
      runner: () => Promise<T>,
      onFinally?: () => void,
    ): Promise<T> => {
      setLoading(true);
      setError(undefined);

      try {
        return await runner();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        onFinally?.();
      }
    },
    [],
  );

  const fetchEmailTemplates =
    useCallback(async (): Promise<EmailTemplateDocuments> => {
      return executeApi(
        setListLoading,
        async () => {
          const params: Record<string, string> = {};
          if (options.filter) {
            if (options.filter.distributionGroupId) {
              params.distributionGroupId = options.filter.distributionGroupId;
            }
            if (options.filter.deliveryTiming) {
              params.deliveryTiming = options.filter.deliveryTiming;
            }
            if (options.filter.name) {
              params.name = options.filter.name;
            }
            if (options.filter.from) {
              params.from = options.filter.from;
            }
          }

          const responseData = await restClient.get<EmailTemplateDocuments>(
            "/emails/templates",
            params,
          );
          setData({ emailTemplates: responseData });
          return responseData;
        },
        options.onCompleted,
      );
    }, [options.filter, options.onCompleted, executeApi]);

  const createEmailTemplate = useCallback(
    async (input: CreateEmailTemplateInput): Promise<EmailTemplate> => {
      return executeApi(setCreateLoading, async () => {
        return restClient.post<EmailTemplate>("/emails/templates", input);
      });
    },
    [executeApi],
  );

  const updateEmailTemplate = useCallback(
    async (
      templateId: string,
      input: UpdateEmailTemplateInput,
    ): Promise<EmailTemplate> => {
      return executeApi(setUpdateLoading, async () => {
        return restClient.patch<EmailTemplate>(
          `/emails/templates/${templateId}`,
          input,
        );
      });
    },
    [executeApi],
  );

  const deleteEmailTemplate = useCallback(
    async (templateId: string) => {
      await executeApi(setDeleteLoading, async () => {
        return restClient.delete<void>(`/emails/templates/${templateId}`);
      });
    },
    [executeApi],
  );

  const initialFetchDone = useRef(false);
  const skipRef = useRef(options.skip);

  useEffect(() => {
    if (!skipRef.current && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchEmailTemplates().catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchEmailTemplates]);

  return {
    data,
    error,
    refetch: fetchEmailTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    listLoading,
    createLoading,
    updateLoading,
    deleteLoading,
  };
}

export default {
  useEmailTemplatesRestApi,
};
